import { useState, useEffect } from 'react'
import { Loader2, Globe, Eye, EyeOff } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { Site, SiteDetail, SiteCreate, SiteUpdate, SiteCredentialsUpdate } from '@/types'

interface SiteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  site?: Site | SiteDetail | null
  onSubmit: (data: SiteCreate | SiteUpdate) => Promise<void>
  onUpdateCredentials?: (data: SiteCredentialsUpdate) => Promise<void>
  isSubmitting: boolean
}

export function SiteModal({
  open,
  onOpenChange,
  site,
  onSubmit,
  onUpdateCredentials,
  isSubmitting,
}: SiteModalProps) {
  const isEditing = !!site

  const [activeTab, setActiveTab] = useState('details')
  const [showPassword, setShowPassword] = useState(false)

  const [formData, setFormData] = useState<SiteCreate>({
    name: '',
    url: '',
    description: '',
    wp_username: '',
    wp_app_password: '',
    default_status: 'draft',
  })

  const [credentials, setCredentials] = useState<SiteCredentialsUpdate>({
    wp_username: '',
    wp_app_password: '',
  })

  useEffect(() => {
    if (site) {
      setFormData({
        name: site.name,
        url: site.url,
        description: site.description || '',
        wp_username: 'wp_username' in site ? site.wp_username : '',
        wp_app_password: '',
        default_category_id: site.default_category_id ?? undefined,
        default_author_id: site.default_author_id ?? undefined,
        default_status: site.default_status || 'draft',
      })
      setCredentials({
        wp_username: 'wp_username' in site ? site.wp_username : '',
        wp_app_password: '',
      })
    } else {
      setFormData({
        name: '',
        url: '',
        description: '',
        wp_username: '',
        wp_app_password: '',
        default_status: 'draft',
      })
      setCredentials({
        wp_username: '',
        wp_app_password: '',
      })
    }
    setActiveTab('details')
    setShowPassword(false)
  }, [site, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isEditing) {
      const updateData: SiteUpdate = {
        name: formData.name,
        url: formData.url,
        description: formData.description,
        default_category_id: formData.default_category_id,
        default_author_id: formData.default_author_id,
        default_status: formData.default_status,
      }
      await onSubmit(updateData)
    } else {
      await onSubmit(formData)
    }
  }

  const handleUpdateCredentials = async (e: React.FormEvent) => {
    e.preventDefault()
    if (onUpdateCredentials) {
      await onUpdateCredentials(credentials)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            {isEditing ? 'Edit Site' : 'Add WordPress Site'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update your WordPress site settings'
              : 'Connect a WordPress site to publish articles'}
          </DialogDescription>
        </DialogHeader>

        {isEditing ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Site Details</TabsTrigger>
              <TabsTrigger value="credentials">Credentials</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Site Name *</Label>
                  <Input
                    id="name"
                    placeholder="My WordPress Blog"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="url">WordPress URL *</Label>
                  <Input
                    id="url"
                    type="url"
                    placeholder="https://example.com"
                    value={formData.url}
                    onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Brief description of this site"
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="default_status">Default Post Status</Label>
                  <Select
                    value={formData.default_status}
                    onValueChange={(value: string) => setFormData((prev) => ({ ...prev, default_status: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="publish">Published</SelectItem>
                      <SelectItem value="pending">Pending Review</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Changes
                  </Button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="credentials" className="space-y-4 mt-4">
              <div className="p-3 bg-muted rounded-md text-sm">
                <p className="font-medium mb-1">How to get Application Password:</p>
                <ol className="list-decimal list-inside text-muted-foreground space-y-1">
                  <li>Log in to your WordPress admin</li>
                  <li>Go to Users → Profile</li>
                  <li>Scroll to "Application Passwords"</li>
                  <li>Create a new password for Geo-CrewAI</li>
                </ol>
              </div>

              <form onSubmit={handleUpdateCredentials} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="wp_username">WordPress Username *</Label>
                  <Input
                    id="wp_username"
                    placeholder="admin"
                    value={credentials.wp_username}
                    onChange={(e) => setCredentials((prev) => ({ ...prev, wp_username: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wp_app_password">Application Password *</Label>
                  <div className="relative">
                    <Input
                      id="wp_app_password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                      value={credentials.wp_app_password}
                      onChange={(e) => setCredentials((prev) => ({ ...prev, wp_app_password: e.target.value }))}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Update Credentials
                  </Button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Site Name *</Label>
              <Input
                id="name"
                placeholder="My WordPress Blog"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">WordPress URL *</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com"
                value={formData.url}
                onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of this site"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">WordPress Authentication</h4>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="wp_username">WordPress Username *</Label>
                  <Input
                    id="wp_username"
                    placeholder="admin"
                    value={formData.wp_username}
                    onChange={(e) => setFormData((prev) => ({ ...prev, wp_username: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wp_app_password">Application Password *</Label>
                  <div className="relative">
                    <Input
                      id="wp_app_password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                      value={formData.wp_app_password}
                      onChange={(e) => setFormData((prev) => ({ ...prev, wp_app_password: e.target.value }))}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Create in WordPress: Users → Profile → Application Passwords
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Site
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
